import { clients } from '@/lib/https';
import { getIamServiceUrl } from '@/lib/api/iam-service-url';

export type ApiKeyRole = {
  itemId: string;
  name: string;
  slug: string;
};

export const ROLES_PAGE_SIZE = 20;

export async function getApiKeyRoles(
  organizationId: string,
  page = 0,
  search = ''
): Promise<{ items: ApiKeyRole[]; totalCount: number }> {
  if (!organizationId.trim()) throw new Error('Select an organization to load roles.');
  if (!Number.isSafeInteger(page) || page < 0) throw new Error('Invalid roles page');
  const url = getIamServiceUrl('/iam/v4/iam/roles');
  const roles = new Map<string, ApiKeyRole>();

  const response = await clients.post<unknown>(
    url,
    JSON.stringify({
      page,
      pageSize: ROLES_PAGE_SIZE,
      organizationId,
      sort: { property: 'Name', isDescending: false },
      filter: { search, resourceGroup: 'blocks-utilities' },
    })
  );
  if (!response || typeof response !== 'object') throw new Error('Invalid roles response');
  const envelope = response as Record<string, unknown>;
  if (envelope.errors && (!Array.isArray(envelope.errors) || envelope.errors.length > 0)) {
    throw new Error('Could not load roles');
  }
  if (
    !Array.isArray(envelope.data) ||
    typeof envelope.totalCount !== 'number' ||
    !Number.isSafeInteger(envelope.totalCount) ||
    envelope.totalCount < 0
  ) {
    throw new Error('Invalid roles response');
  }
  for (const value of envelope.data) {
    if (!value || typeof value !== 'object') throw new Error('Invalid role');
    const item = value as Record<string, unknown>;
    if (
      typeof item.itemId !== 'string' ||
      !item.itemId ||
      typeof item.name !== 'string' ||
      typeof item.slug !== 'string' ||
      !item.slug.trim()
    ) {
      throw new Error('Invalid role');
    }
    roles.set(item.itemId, {
      itemId: item.itemId,
      name: item.name || item.slug,
      slug: item.slug,
    });
  }
  return { items: [...roles.values()], totalCount: envelope.totalCount };
}
